import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { formatDate } from '../utils/dates';
import { t } from '../utils/translations';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Products.css';

const Subcategories = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subcategoriesRes, categoriesRes] = await Promise.all([
        axios.get('/api/subcategories'),
        axios.get('/api/categories')
      ]);

      setSubcategories(subcategoriesRes.data.subcategories || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert(t('Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await axios.put(`/api/subcategories/${editingSubcategory.id}`, formData);
      } else {
        await axios.post('/api/subcategories', formData);
      }

      setShowModal(false);
      setEditingSubcategory(null);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save subcategory'));
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      category_id: subcategory.category_id || '',
      name: subcategory.name || '',
      description: subcategory.description || '',
      status: subcategory.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this subcategory?'))) {
      return;
    }

    try {
      await axios.delete(`/api/subcategories/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to delete subcategory'));
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      name: '',
      description: '',
      status: 'active'
    });
  };

  const openModal = () => {
    resetForm();
    setEditingSubcategory(null);
    setShowModal(true);
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('Subcategories')}</h1>
          <button onClick={openModal} className="btn btn-primary">
            {t('Add Subcategory')}
          </button>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('Category')}</th>
                <th>{t('Name')}</th>
                <th>{t('Description')}</th>
                <th>{t('Status')}</th>
                <th>{t('Created')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {subcategories.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center' }}>{t('No subcategories found')}</td>
                </tr>
              ) : (
                subcategories.map(subcategory => (
                  <tr key={subcategory.id}>
                    <td>{subcategory.category_name || '-'}</td>
                    <td>{subcategory.name}</td>
                    <td>{subcategory.description || '-'}</td>
                    <td>
                      <span className={`status-badge status-${subcategory.status}`}>
                        {subcategory.status}
                      </span>
                    </td>
                    <td>{formatDate(subcategory.created_at)}</td>
                    <td>
                      <KebabMenu
                        menuId={`subcategory-${subcategory.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        label={t('Actions')}
                        items={[
                          { label: t('Edit'), icon: MENU_ICONS.edit, onClick: () => handleEdit(subcategory) },
                          { label: t('Delete'), icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(subcategory.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingSubcategory ? t('Edit Subcategory') : t('Add Subcategory')}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('Category')} *</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                  >
                    <option value="">{t('Select Category')}</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('Name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('Description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>{t('Status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="inactive">{t('Inactive')}</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingSubcategory ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subcategories;
